import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {PortalService} from "../../account-settings/portal/my-portals/portal.service";
import {ActivatedRoute, Router} from "@angular/router";
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import { PaymentPopupComponent } from 'src/app/account-settings/plans/payment-popup/payment-popup.component';
import { ToastService } from 'src/app/services/notification/toast.service';
import { WebService } from 'src/app/services/web/web.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { SecurityService } from 'src/app/services/security.service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MAT_DATE_LOCALE, MatDateFormats, provideNativeDateAdapter } from '@angular/material/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { getManageStatusErrorMessage } from 'src/app/shared/utils/manage-status-error';
import {
  canUsePortalService,
  isInactiveOnboardingPortal,
  shouldShowPaymentButtonForPortal
} from 'src/app/shared/utils/onboarding-payment-status';

const LAUNCH_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: null },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' }
  }
};



@Component({
    selector: 'app-my-portal',
    templateUrl: './my-portal.component.html',
    styleUrls: ['./my-portal.component.scss'],
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatMenuModule, MatButtonModule, MatIconModule, MatDatepickerModule, MatInputModule, TranslateModule],
    providers: [
      { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
      provideNativeDateAdapter(LAUNCH_DATE_FORMATS)
    ]
})
export class MyPortalComponent implements OnChanges{
  @ViewChild('launchConfirmationDialog') launchConfirmationDialog?: TemplateRef<unknown>;
  public myportals : any = [];
  @Input() portal: any;
  configID : any;
  isPortalActive : boolean = true;
  iframeSrc!: SafeResourceUrl;
  showPaymentButton : Boolean = false;
  launchConfirmationValue = '';
  launchTargetPortal: any;
  editingLaunchDate = false;
  launchDateDraft: Date | null = null;
  savingLaunchDate = false;
  showLaunchDateEditAction = false;
  readonly maxLaunchDate = new Date();
  private launchDialogRef?: MatDialogRef<unknown>;
  private destroyRef = inject(DestroyRef);
  constructor(
    private webService : WebService,
    private spinner : SpinnerService,
    private toast : ToastService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private router : Router, 
    private activateRoute : ActivatedRoute,
    private portalService : PortalService,
    private securityService: SecurityService,
    private translate: TranslateService) {
    
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['portal']) {
      this.handleIsActiveChange();
      if (this.portal && this.portal.url_template) {
        this.iframeSrc = this.getUrlTemplatePreview(this.portal.url_template);
      }
    }
    if (changes['portal'] && this.portal && this.portal.url_template) {
      this.iframeSrc = this.getUrlTemplatePreview(this.portal.url_template);
    }
  }
  onButtonClick(portal: any, mode: 'subscription' | 'onetime_payment'): void {
    if (!this.canUsePortal(portal)) {
      this.toast.showError(this.translate.instant('myPortal.messages.activateRequired'));
    } else {
      if (mode === 'onetime_payment' && !portal.oneTimePlanId) {
        this.toast.showInfo(this.translate.instant('myPortal.messages.noOnetimePlan'));
        return;
      }
      this.goAddClient(portal, mode);
    }
  }

  canUsePortal(portal: any = this.portal): boolean {
    return canUsePortalService(portal);
  }

  shouldShowPaymentButton(portal: any = this.portal): boolean {
    return shouldShowPaymentButtonForPortal(portal);
  }

  canActivateOnboardingPortal(portal: any = this.portal): boolean {
    return isInactiveOnboardingPortal(portal);
  }

  isLaunchedToProduction(portal: any = this.portal): boolean {
    return !!portal?.service?.launched_at;
  }

  canLaunchToProduction(portal: any = this.portal): boolean {
    return this.launchToProductionEnabled && !!portal?.service?.id && !this.isLaunchedToProduction(portal);
  }

  canEditLaunchDate(portal: any = this.portal): boolean {
    return this.launchToProductionEnabled && !!portal?.service?.id && this.isLaunchedToProduction(portal);
  }

  get launchToProductionEnabled(): boolean {
    return environment.launchToProductionEnabled && this.securityService.isSuperAdmin();
  }

  getLaunchUserName(portal: any = this.portal): string {
    return portal?.service?.launched_by?.email || portal?.service?.launched_by?.name || '';
  }

  get launchConfirmationMatches(): boolean {
    return this.launchConfirmationValue.trim() === (this.launchTargetPortal?.service?.name_service || '').trim();
  }

  getUrlTemplateWithIdentifier(): string {
    if (!this.portal?.url_template || !this.portal?.portal_identifier) return '';
    return `${this.portal.url_template}/${this.portal.portal_identifier}`;
  }
  ngOnInit(): void {

    this.portalService.portals
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(portals => {
        this.myportals = [...portals];
      });

    this.isPortalActive = this.portal.isActive;

  }
  private handleIsActiveChange(): void {
    if (this.portal?.isActive !== undefined) {
     
    }
  }


  goEdit(portal : any){
    this.router.navigate(["/edit/portal/" + portal.id], {relativeTo : this.activateRoute, queryParamsHandling : "preserve"});
  }

  goEditPlans(portal : any){
    this.router.navigate(["/edit/portal/" + portal.id + "/my-plans"], {relativeTo : this.activateRoute, queryParamsHandling : "preserve"});
  }

  goAddClient(portal: any, mode: 'subscription' | 'onetime_payment' = 'subscription') {
    this.router.navigate(
      ["/portal/" + portal.id + "/client"],
      {
        relativeTo: this.activateRoute,
        queryParams: { mode }, // aquí añadimos el parámetro
        queryParamsHandling: "merge" // para preservar lo anterior y agregar `mode`
      }
    );
  }
  goCreatePlan(){
    this.router.navigate(['create'], {relativeTo : this.activateRoute, queryParamsHandling : "preserve"});
  }

  goToPortal(portal : any) {
    window.open(portal, '_blank');
  }


  getUrlTemplatePreview(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }


  goToTemplates(portal : any, update : Boolean){
    if(update){
      this.router.navigate(["/edit/portal/" +portal.id+'/portal'], {queryParams: { update: true }}); 
    }else{
      this.router.navigate(["/edit/portal/" +portal.id+'/portal']); 
    }
    
  }
  goToFeatures(portal : any){
    this.router.navigate(["/edit/portal/" +portal.id+'/features'], {queryParams: { update: true }}); 
  }

  openLaunchConfirmation(portal: any): void {
    if (!this.launchConfirmationDialog || !this.canLaunchToProduction(portal)) {
      return;
    }

    this.launchTargetPortal = portal;
    this.launchConfirmationValue = '';
    this.launchDialogRef = this.dialog.open(this.launchConfirmationDialog, {
      autoFocus: false,
      disableClose: true,
      maxWidth: '520px',
      width: '92vw'
    });
  }

  cancelLaunchConfirmation(): void {
    this.launchDialogRef?.close();
    this.launchDialogRef = undefined;
    this.launchTargetPortal = undefined;
    this.launchConfirmationValue = '';
  }

  startLaunchDateEdit(portal: any = this.portal): void {
    if (!this.canEditLaunchDate(portal)) {
      return;
    }

    const inputDate = this.getLaunchDateInputValue(portal?.service?.launched_at);
    if (!inputDate) {
      this.toast.showError(this.translate.instant('myPortal.launch.messages.invalidDate'));
      return;
    }

    this.launchDateDraft = inputDate;
    this.editingLaunchDate = true;
  }

  cancelLaunchDateEdit(): void {
    this.editingLaunchDate = false;
    this.launchDateDraft = null;
  }

  setLaunchDateEditActionVisible(visible: boolean): void {
    this.showLaunchDateEditAction = visible;
  }

  onLaunchDateChange(value: Date | null): void {
    this.launchDateDraft = value;
  }

  saveLaunchDate(portal: any = this.portal): void {
    const serviceId = portal?.service?.id;
    const launchedAt = this.parseLaunchDate(this.launchDateDraft);
    if (!serviceId || !this.canEditLaunchDate(portal)) {
      return;
    }
    if (!launchedAt) {
      this.toast.showError(this.translate.instant('myPortal.launch.messages.invalidDate'));
      return;
    }

    this.savingLaunchDate = true;
    this.webService.put({ launchedAt }, `${this.webService.HOST}/api/v1/services/${serviceId}/launch`).subscribe({
      next: response => {
        const updatedService = response?.result || response || {};
        portal.service = {
          ...portal.service,
          ...updatedService,
          launched_at: updatedService.launched_at ?? updatedService.launchedAt ?? portal.service.launched_at
        };
        this.savingLaunchDate = false;
        this.cancelLaunchDateEdit();
        this.toast.showSuccess(response?.message || this.translate.instant('myPortal.launch.messages.dateUpdated'));
      },
      error: err => {
        this.savingLaunchDate = false;
        if (err?.error?.code === 'future_date_not_allowed') {
          this.toast.showError(this.translate.instant('myPortal.launch.messages.futureDateNotAllowed'));
          return;
        }
        this.toast.showError(err?.error?.message || this.translate.instant('myPortal.launch.messages.dateUpdateError'));
      }
    });
  }

  private getLaunchDateInputValue(value: unknown): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) {
      return null;
    }
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private parseLaunchDate(value: Date | null): string | null {
    if (!value || Number.isNaN(value.getTime())) {
      return null;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  confirmLaunchToProduction(): void {
    const portal = this.launchTargetPortal;
    const serviceId = portal?.service?.id;
    if (!serviceId || !this.launchConfirmationMatches) {
      return;
    }

    this.cancelLaunchConfirmation();
    this.spinner.open();
    this.webService.post({}, `${this.webService.HOST}/api/v1/services/${serviceId}/launch`).subscribe({
      next: response => {
        this.spinner.close();
        const updatedService = response?.result || response;
        portal.service = {
          ...portal.service,
          ...updatedService
        };
        this.toast.showSuccess(response?.message || this.translate.instant('myPortal.launch.messages.success'));
      },
      error: err => {
        this.spinner.close();
        const status = err?.status;
        if (status === 409) {
          this.toast.showError(this.translate.instant('myPortal.launch.messages.conflict'));
        } else if (status === 403) {
          this.toast.showError(this.translate.instant('myPortal.launch.messages.forbidden'));
        } else {
          this.toast.showError(err);
        }
      }
    });
  }

  goDeactivatePortal(portal: any){
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '600px',
    
    });
  
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.spinner.open();
        const data = { id: portal.id, type: 2 };
        this.webService.post(data, this.webService.HOST + "/onboarding/manageStatus").subscribe(response => {
          this.spinner.close();
          this.toast.showSuccess(this.translate.instant('myPortal.messages.deactivateSuccess'));
          window.location.reload();
        }, err => {
          this.spinner.close();
          console.log(err);
          this.toast.showError(getManageStatusErrorMessage(err));
        });
      } else {
        this.spinner.close();
      }
    });
  }

  goActivatePortal(portal: any): void {
    this.spinner.open();
    const data = { id: portal.id, type: 1 };

    this.webService.post(data, this.webService.HOST + '/onboarding/manageStatus').subscribe({
      next: () => {
        this.spinner.close();
        this.toast.showSuccess(this.translate.instant('myPortal.messages.activateSuccess'));
        this.reloadPage();
      },
      error: err => {
        this.spinner.close();
        this.toast.showError(getManageStatusErrorMessage(err));
      }
    });
  }

  reloadPage(): void {
    window.location.reload();
  }
  

  processPayment(portal : any){
    let dialogRef: MatDialogRef<any> = this.dialog.open(PaymentPopupComponent, {
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        configID: portal.id,
        type : 1
      }
    })
    dialogRef.afterClosed()
      .subscribe(res => {
        if(res != undefined &&  res == 1){// SOLO SI ES COBRADO
          this.router.navigateByUrl("/congratulation");
        }else{
          window.location.reload();
        }
      })
  }

}
